import React, { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { TextField, Typography, Grid, Button, IconButton } from '@material-ui/core';
import { SearchSelect } from 'lib';
import CloseIcon from '@material-ui/icons/Close';
import { useDispatch, useSelector } from 'react-redux';
import propTypes from 'prop-types';

import { isIOS, Option } from 'components/Option';
import {
  createOptions,
  createOptionsWithLabel,
  createListWithOptions,
  createOptionsWithLabelMenuItems,
} from 'helpers/createOptions';
import { useTimeoffsStyles } from 'pages/Availability/components/Timeoffs/components/SortingAndFilteringPanel/useStyles';
import schedulePageActions from 'store/schedule/schedulePageActions';
import { statusOptions, shiftOptions, certifiedPayrollOptions, forceAccountOptions } from './filter-options';
import { getOptions } from 'helpers/getOptions';
import { $fetch } from 'http/fetch';
import { getProjectName } from 'helpers/_helpers';
import classnames from 'classnames';

export const ScheduleFilters = ({ closeDrawer, mobileStyles = {} }) => {
  const dispatch = useDispatch();
  const classes = { ...useTimeoffsStyles(), ...mobileStyles };
  const { workers, equipment } = useSelector((state) => state.schedule.resources || {});
  const defaultFilters = useSelector((state) => state.schedule.filters);
  const {
    contractors: contractorOptions,
    projects: projectOptions,
    projectTypes: projectTypesOptions,
  } = useSelector((state) => state.schedule.filterOptions || {});
  const showAllLocations = useSelector((state) => state.schedule.showAllLocations);
  const locationSettings = useSelector((state) => state.personalProfile?.organizationLocation?.settings);
  const personIdOptions = useMemo(
    () =>
      [{ _id: 'All', username: 'All' }, ...workers].map((w) => ({
        value: w._id,
        label: w.username,
      })),
    [workers]
  );

  const equipmentIdOptions = useMemo(
    () =>
      [{ _id: 'All', username: 'All' }, ...equipment].map((e) => ({
        value: e._id,
        label: e.name,
      })),
    [equipment]
  );

  const [filteredProjectOptions, setFilteredProjectOptions] = useState(
    projectOptions
      .filter((p) => p.contractorId === defaultFilters.contractorId)
      .filter((p) => (
        defaultFilters.projectType === 'All' ||
        !defaultFilters.projectType ||
        p.type === defaultFilters.projectType
      ))
      .map((p) => ({ value: p._id, type: p.type, label: getProjectName(p) }))
  );

  const {
    handleSubmit,
    control,
    watch,
    formState: { errors },
    reset,
  } = useForm({
    reValidateMode: 'onChange',
    defaultValues: {
      status: defaultFilters.status || [statusOptions?.[0]?.value] || ['All'],
      shift: shiftOptions[0],
      userId: defaultFilters.userId || 'All',
      equipment: defaultFilters.equipment || 'All',
      contractorId: defaultFilters.contractorId || 'All',
      projectId: defaultFilters.projectId || 'All',
      projectType: defaultFilters.projectType || 'All',
      rated: defaultFilters.rated || 'All',
      forceAccount: forceAccountOptions[0].value,
      ...defaultFilters,
    },
  });

  const selectedContractorId = watch('contractorId');
  const selectedProjectType = watch('projectType');


  useEffect(() => {
    const fetchContractors = async () => {
      try {
        const searchParams = `?isWithoutSubmissionClone=1${showAllLocations ? '&allLocations=true' : ''}`
        const response = await $fetch(
          `${process.env.REACT_APP_BASE_URL}/contractors${searchParams}`,
          {
            ...getOptions(),
            method: 'GET',
          }
        ).then((res) => res.json());

        if (response.statusCode > 299) throw new Error(response.message);
        dispatch(
          schedulePageActions.setFilterOptions({
            contractors: response
              .sort((a, b) => a.name.localeCompare(b.name))
              .map((c) => ({ value: c._id, label: c.name })),
          })
        );
      } catch (e) {
        console.log(e);
        dispatch(schedulePageActions.setFilterOptions({ contractors: [] }));
      }
    };
    const fetchProjects = async () => {
      try {
        const response = await $fetch(
          `${process.env.REACT_APP_BASE_URL}/projects?isWithoutSubmissionClone=1`,
          {
            ...getOptions(),
            method: 'GET',
          }
        ).then((res) => res.json());

        if (response.statusCode > 299) throw new Error(response.message);
        dispatch(
          schedulePageActions.setFilterOptions({
            projects: response.projects?.slice()?.sort((a, b) => a.route.localeCompare(b.route)),
          })
        );
      } catch (e) {
        console.log(e);
        dispatch(schedulePageActions.setFilterOptions({ projects: [] }));
      }
    };
    const fetchProjectTypes = async () => {
      try {
        const response = await $fetch(
          `${process.env.REACT_APP_BASE_URL}/project-types`,
          {
            ...getOptions(),
            method: 'GET',
          }
        ).then((res) => res.json());

        if (response.statusCode > 299) throw new Error(response.message);
        dispatch(
          schedulePageActions.setFilterOptions({
            projectTypes: response?.slice()?.sort((a, b) => a.name.localeCompare(b.name)),
          })
        );
      } catch (e) {
        console.log(e);
        dispatch(schedulePageActions.setFilterOptions({ projects: [] }));
      }
    };
    if (!contractorOptions.length) fetchContractors();
    if (!projectOptions.length) fetchProjects();
    if (!projectTypesOptions.length) fetchProjectTypes();
  }, []);

  useEffect(() => {
    if (!selectedContractorId || selectedContractorId === 'All') {
      setFilteredProjectOptions([]);
    }

    setFilteredProjectOptions(
      projectOptions
        .filter((p) => p.contractorId === selectedContractorId)
        .filter((p) => (
          selectedProjectType === 'All' ||
          !selectedProjectType ||
          p.type === selectedProjectType
        ))
        .map((p) => ({ value: p._id, type: p.type, label: getProjectName(p) }))
    );
  }, [selectedContractorId, selectedProjectType]);


  const onSubmit = (data) => {
    const normalizedData = Object.entries(data)
      .filter(([, value]) => value !== 'All' && (!Array.isArray(value) || value.length > 0))
      .reduce((res, [key, value]) => ({ ...res, [key]: value }), {});
    if (normalizedData.status.length === statusOptions.length) delete normalizedData.status;
    if (!locationSettings?.timeclock && normalizedData.rated?.length === certifiedPayrollOptions?.length) delete normalizedData.rated;
    dispatch(schedulePageActions.setFilters(normalizedData));
    closeDrawer();
  };

  const contractorOptionsEx = [{ value: 'All', label: 'All' }, ...contractorOptions];
  const filteredProjectOptionsEx = [{ value: 'All', label: 'All' }, ...filteredProjectOptions];
  const filteredProjectTypeOptionsEx = ['All', ...projectTypesOptions.map(option => option.name)];

  return (
    <div className={classes.formWrapper} role="presentation">
      <form
        onSubmit={handleSubmit(onSubmit)}
        className={classnames(classes.formFlex, classes?.mobileSpace)}
      >
        <div>
          <Typography variant="h3" className={classes.marginHeader}>
            Filters
          </Typography>
          <IconButton
            className={classes.closeButton}
            onClick={closeDrawer}
            aria-label="Close"
            disableTouchRipple
          >
            <CloseIcon />
          </IconButton>
          <Grid container spacing={2} className={classes.flexContainer}>
            <Grid item xs={12} lg={6} className={classes.fieldWrapper}>
              <label htmlFor="status">
                <Typography variant="body1" color="textSecondary">
                  Status:
                </Typography>
                <Controller
                  name="status"
                  control={control}
                  rules={{
                    validate: (value) => !!value.length || 'Please, select at least 1',
                  }}
                  render={({ value, onChange }) => (
                    <TextField
                      select
                      id="status"
                      name="status"
                      variant="outlined"
                      fullWidth
                      error={Boolean(errors.status)}
                      value={value}
                      onChange={(e) => {
                        let eventValue = e.target.value;
                        if (typeof eventValue === "string") {
                          eventValue = eventValue.split(",")
                        }
                        // if value was 'All' -> change to new value
                        if (value?.includes('All')) return onChange(eventValue?.filter(v => v !== 'All') || []);
                        // if next value contains 'All' -> change value to 'All'
                        else if (eventValue?.includes('All') && eventValue.length > 1) return onChange(['All']);
                        return onChange(eventValue);
                      }}
                      SelectProps={{
                        multiple: true,
                        // this makes an input buggy - native: isIOS,
                        renderValue: (selected) => selected.join(', '),
                        MenuProps: {
                          getContentAnchorEl: null,
                        }
                      }}
                    >
                      {statusOptions.map(({ value, label }) =>
                        createOptionsWithLabelMenuItems({ value, label })
                      )}
                    </TextField>
                  )}
                />
                {errors.status ? (
                  <div className={classes.error}>{errors.status.message}</div>
                ) : null}
              </label>
            </Grid>
            <Grid item xs={12} lg={6} className={classes.fieldWrapper}>
              <label htmlFor="shift">
                <Typography variant="body1" color="textSecondary">
                  Shift:
                </Typography>
                <Controller
                  name="shift"
                  control={control}
                  as={
                    <TextField
                      select
                      id="shift"
                      name="shift"
                      variant="outlined"
                      fullWidth
                      error={Boolean(errors.shift)}
                      SelectProps={{
                        native: isIOS,
                        MenuProps: {
                          getContentAnchorEl: null,
                        }
                      }}
                    >
                      {shiftOptions.map(createOptions)}
                    </TextField>
                  }
                />
                {errors.shift ? <div className={classes.error}>{errors.shift.message}</div> : null}
              </label>
            </Grid>
            <Grid item xs={12} lg={6} className={classes.fieldWrapper}>
              <label htmlFor="userId">
                <Typography variant="body1" color="textSecondary">
                  Person:
                </Typography>
                <Controller
                  name="userId"
                  control={control}
                  render={({ onChange, value }) => {
                    return (
                      <SearchSelect
                        value={value}
                        onChange={(id) => id && onChange(id)}
                        options={personIdOptions}
                        getOptionId={(option) => option.value}
                        getOptionLabel={(option) => option.label || 'All'}
                        renderOption={({ value, label }) => createListWithOptions({ value, label })}
                        inputProps={{ id: 'userId', name: 'userId', 'aria-label': 'Person' }}
                      />
                    );
                  }}
                />
                {errors.userId ? (
                  <div className={classes.error}>{errors.userId.message}</div>
                ) : null}
              </label>
            </Grid>
            <Grid item xs={12} lg={6} className={classes.fieldWrapper}>
              <label htmlFor="equipment">
                <Typography variant="body1" color="textSecondary">
                  Equipment:
                </Typography>
                <Controller
                  name="equipment"
                  control={control}
                  render={({ onChange, value }) => {
                    return (
                      <SearchSelect
                        value={value}
                        onChange={(id) => id && onChange(id)}
                        options={equipmentIdOptions}
                        getOptionId={(option) => option.value}
                        getOptionLabel={(option) => option.label || 'All'}
                        renderOption={({ value, label }) => createListWithOptions({ value, label })}
                        inputProps={{ id: 'equipment', name: 'equipment', 'aria-label': 'Equipment' }}
                      />
                    );
                  }}
                />
                {errors.equipment ? (
                  <div className={classes.error}>{errors.equipment.message}</div>
                ) : null}
              </label>
            </Grid>
            <Grid item xs={12} lg={6} className={classes.fieldWrapper}>
              <label htmlFor="contractorId">
                <Typography variant="body1" color="textSecondary">
                  Contractor:
                </Typography>
                <Controller
                  name="contractorId"
                  control={control}
                  render={({ onChange, value }) => {
                    return (
                      <SearchSelect
                        value={value}
                        onChange={(id) => id && onChange(id)}
                        options={contractorOptionsEx}
                        getOptionId={(option) => option.value}
                        getOptionLabel={(option) => option.label || 'All'}
                        renderOption={({ value, label }) => createListWithOptions({ value, label })}
                        inputProps={{ id: 'contractorId', name: 'contractorId', 'aria-label': 'Contractor' }}
                      />
                    );
                  }}
                />
                {errors.contractorId ? (
                  <div className={classes.error}>{errors.contractorId.message}</div>
                ) : null}
              </label>
            </Grid>
            <Grid item xs={12} lg={6} className={classes.fieldWrapper}>
              <label htmlFor="projectId">
                <Typography variant="body1" color="textSecondary">
                  Project:
                </Typography>
                <Controller
                  name="projectId"
                  id="projectId"
                  control={control}
                  render={({ onChange, value }) => {
                    return (
                      <SearchSelect
                        value={value}
                        onChange={(id) => id && onChange(id)}
                        options={filteredProjectOptionsEx}
                        getOptionId={(option) => option.value}
                        getOptionLabel={(option) => option.label || 'All'}
                        renderOption={({ value, label }) => createListWithOptions({ value, label })}
                        inputProps={{ id: 'projectId', name: 'projectId', 'aria-label': 'Project' }}
                      />
                    );
                  }}
                />
                {errors.projectId ? (
                  <div className={classes.error}>{errors.projectId.message}</div>
                ) : null}
              </label>
            </Grid>
            <Grid item xs={12} lg={6} className={classes.fieldWrapper}>
              <label htmlFor="projectType">
                <Typography variant="body1" color="textSecondary">
                  Project Type:
                </Typography>
                <Controller
                  name="projectType"
                  id="projectType"
                  control={control}
                  render={({ onChange, value }) => {
                    return (
                      <SearchSelect
                        value={value}
                        onChange={(type) => type && onChange(type)}
                        options={filteredProjectTypeOptionsEx}
                        getOptionId={(option) => option}
                        getOptionLabel={(option) => option || 'All'}
                        renderOption={(option) => createListWithOptions({ value: option, label: option })}
                        inputProps={{ id: 'projectType', name: 'projectType', 'aria-label': 'Project Type' }}
                      />
                    );
                  }}
                />
                {errors.projectType ? (
                  <div className={classes.error}>{errors.projectType.message}</div>
                ) : null}
              </label>
            </Grid>
            {locationSettings?.timeclock && (
              <Grid item xs={12} lg={6} className={classes.fieldWrapper}>
                <label htmlFor="rated">
                  <Typography variant="body1" color="textSecondary">
                    Certified Payroll:
                  </Typography>
                  <Controller
                    name="rated"
                    control={control}
                    rules={{
                      validate: (value) => !!value.length || 'Please, select at least 1',
                    }}
                    render={({ value, onChange }) => (
                      <TextField
                        select
                        id="rated"
                        name="rated"
                        variant="outlined"
                        fullWidth
                        error={Boolean(errors.rated)}
                        value={value}
                        onChange={(e) => {
                          const eventValue = e.target.value;
                          onChange(eventValue);
                        }}
                        SelectProps={{
                          native: isIOS,
                          MenuProps: {
                            getContentAnchorEl: null,
                          }
                        }}
                      >
                        {certifiedPayrollOptions.map(({ value, label }) =>
                          createOptionsWithLabel({ value, label })
                        )}
                      </TextField>
                    )}
                  />
                  {errors.rated ? <div className={classes.error}>{errors.rated.message}</div> : null}
                </label>
              </Grid>
            )}
            <Grid item xs={12} lg={6} className={classes.fieldWrapper}>
              <label htmlFor="forceAccount">
                <Typography variant="body1" color="textSecondary">
                  T&M:
                </Typography>
                <Controller
                  name="forceAccount"
                  control={control}
                  as={
                    <TextField
                      select
                      id="forceAccount"
                      name="forceAccount"
                      variant="outlined"
                      fullWidth
                      error={Boolean(errors.forceAccount)}
                      SelectProps={{
                        native: isIOS,
                        MenuProps: {
                          getContentAnchorEl: null,
                        }
                      }}
                    >
                      {forceAccountOptions.map(({ value, label }) =>
                        createOptionsWithLabel({ value, label })
                      )}
                    </TextField>
                  }
                />
                {errors.forceAccount ? (
                  <div className={classes.error}>{errors.forceAccount.message}</div>
                ) : null}
              </label>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Button
              color="primary"
              className={classes.resetButton}
              onClick={() => {
                reset();
                reset({
                  status: [statusOptions?.[0]?.value] || ['All'],
                  shift: shiftOptions[0],
                  userId: 'All',
                  equipment: 'All',
                  contractorId: 'All',
                  projectId: 'All',
                  rated: ['All'] || [certifiedPayrollOptions?.[0]?.value],
                  forceAccount: forceAccountOptions[0].value,
                });
              }}
              disableTouchRipple
            >
              Reset all filters
            </Button>
          </Grid>
        </div>
        <div className={classes.buttonHolder}>
          <Button color="secondary" className={classes.cancelButton} onClick={closeDrawer}>
            Cancel
          </Button>

          <div className={classes.verticalDivider} />

          <Button type="submit" color="primary" className={classes.saveButton}>
            Apply
          </Button>
        </div>
      </form>
    </div>
  );
};

ScheduleFilters.propTypes = {
  closeDrawer: propTypes.func.isRequired,
};
